import { createContext, Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { WorkflowOrganisationAccessLevel } from "../../../../../constants";
import { getProjectPhaseLabel, ProjectPhase } from "../../../../../constants/project-phase";
import {
  ProjectDates,
  ProjectDatesData,
  SelectData,
  WorkflowOrganisation,
  WorkflowStepModel,
} from "../../../../../models";
import { getDeal, GetDealResponse, GetTaskBoardResponse } from "../../../../../service/query";
import { getTaskBoard, getWorkflowTemplate } from "../../../../../service/query/QueryService.full";
import { Status } from "../../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../../service/ValidationErrorFormatter";
import { useAuth } from "../../../../../useAuth";
import {
  getWorkflowStageKey,
  hasWorkflowOrganisationAccessLevel,
  jsonReviver,
  stringKeyToSentenceCase,
} from "../../../../../utils";
import { Toast } from "../../../../../widget";
import { DealStageItem } from "../../../../asset-manager/deal/components";
import {
  filterWorkflowSteps,
  getDefaultDealStage,
  normaliseWorkflowTemplatePhases,
  parseDealDates,
  parseDealStages,
  parseProjectCachedPhase,
  resolveTasksBoardFallbackRoute,
  WorkflowTemplatePhaseItem,
} from "./utils";

type TasksBoardPageRouteParams = "workflowUuid";

interface TasksBoardProviderProps {
  children?: JSX.Element;
}

interface TasksBoardContextType {
  projectUuid: string | undefined;
  isDealWorkflow: boolean;
  projectCachedPhase: ProjectPhase | undefined;
  workflowUuid: string | undefined;
  tasksBoard: GetTaskBoardResponse | undefined;
  deal: GetDealResponse | undefined;
  workflowSteps: WorkflowStepModel[] | undefined;
  workflowOrganisationAccessLevel: WorkflowOrganisationAccessLevel | undefined;
  workflowOrganisations: WorkflowOrganisation[] | undefined;
  workflowTemplatePhases: WorkflowTemplatePhaseItem[];
  dealStages: DealStageItem[];
  dealDates: Record<string, Record<string, unknown> | null> | undefined;
  currentDealStage: string | undefined;
  workflowStageOptions: SelectData;
  workflowStageLabel: string;

  showComplete: boolean;
  handleShowCompleteChange: () => void;

  showCompletePhaseModal: boolean;
  setShowCompletePhaseModal: Dispatch<SetStateAction<boolean>>;

  showCompleteStageModal: boolean;
  setShowCompleteStageModal: Dispatch<SetStateAction<boolean>>;

  showEditDatesPanel: boolean;
  setShowEditDatesPanel: Dispatch<SetStateAction<boolean>>;

  projectDates: ProjectDatesData | undefined;
  setProjectDates: Dispatch<SetStateAction<ProjectDatesData | undefined>>;

  // Board view switch: "workflow" shows the phase/step Kanban, "general" shows the combined task grid.
  boardView: "workflow" | "general";
  setBoardView: Dispatch<SetStateAction<"workflow" | "general">>;

  currentPhase: ProjectPhase;
  handlePhaseChange: (phase: ProjectPhase) => void;
  handleDealStageChange: (stage: string) => void;
  selectedWorkflowStage: string | undefined;
  isRepeatableWorkflowStage: (stage: string | null | undefined) => boolean;

  hasAccessLevelAndCanEditTemplate: (accessLevel: WorkflowOrganisationAccessLevel) => boolean;
  canEditStep: (createdByUserUuid: string | null) => boolean;

  refreshTasksBoard: boolean;
  shouldRefreshTasksBoard: () => void;
}

export const TasksBoardContext = createContext<TasksBoardContextType>({} as TasksBoardContextType);

export const TasksBoardProvider = ({ children }: TasksBoardProviderProps): JSX.Element => {
  const { currentOrganisationUuid, currentUserType } = useAuth();
  const { workflowUuid: routeWorkflowUuid } = useParams<TasksBoardPageRouteParams>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(true);

  const [showCompletePhaseModal, setShowCompletePhaseModal] = useState(false);
  const [showCompleteStageModal, setShowCompleteStageModal] = useState(false);
  const [showEditDatesPanel, setShowEditDatesPanel] = useState(false);

  const [refreshTasksBoard, setRefreshTasksBoard] = useState(false);
  const shouldRefreshTasksBoard = (): void => {
    setRefreshTasksBoard((prev) => !prev);
  };

  const [tasksBoard, setTasksBoard] = useState<GetTaskBoardResponse | undefined>();
  const [deal, setDeal] = useState<GetDealResponse | undefined>();
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStepModel[] | undefined>();
  const [projectDates, setProjectDates] = useState<ProjectDatesData | undefined>(undefined);
  const [projectCachedPhase, setProjectCachedPhase] = useState<ProjectPhase | undefined>(undefined);
  const [workflowOrganisationAccessLevel, setWorkflowOrganisationAccessLevel] =
    useState<WorkflowOrganisationAccessLevel>();
  const [workflowOrganisations, setWorkflowOrganisations] = useState<WorkflowOrganisation[] | undefined>();
  const [workflowTemplatePhases, setWorkflowTemplatePhases] = useState<WorkflowTemplatePhaseItem[]>([]);
  const [showComplete, setShowComplete] = useState(false);
  const handleShowCompleteChange = (): void => {
    setShowComplete((prev) => !prev);
    shouldRefreshTasksBoard();
  };

  const [dealStages, setDealStages] = useState<DealStageItem[]>([]);
  const [dealDates, setDealDates] = useState<Record<string, Record<string, unknown> | null> | undefined>();
  const [currentDealStage, setCurrentDealStage] = useState<string | undefined>();

  const [currentPhase, setCurrentPhase] = useState<ProjectPhase>(ProjectPhase.feasibility);
  /*
    A link can open the board straight on the General view via ?view=general — a deal's general tasks are
    only listed there, so that's where a general-task link for a deal lands (see resolveTasksBoardFallbackRoute).
  */
  const viewParam = searchParams.get("view");
  const [boardView, setBoardView] = useState<"workflow" | "general">(viewParam === "general" ? "general" : "workflow");
  /*
    The fallback redirect below lands on this same route pattern, so React Router keeps this provider
    mounted and the initialiser above never runs again. Follow the param instead: without this a deal's
    general task opens its overlay over the Workflow view, and closing it strands the user on a view that
    does not list the task. The view toggle sets this state directly and leaves the param alone, so a
    manual switch does not re-run this.
  */
  useEffect(() => {
    setBoardView(viewParam === "general" ? "general" : "workflow");
  }, [viewParam]);

  const handlePhaseChange = (phase: ProjectPhase, steps?: WorkflowStepModel[]): void => {
    setCurrentPhase(parseProjectCachedPhase(phase) as ProjectPhase);
    setWorkflowSteps(filterWorkflowSteps(steps || tasksBoard?.results[0].steps, getProjectPhaseLabel(phase)));
  };
  const handleDealStageChange = (stage: string): void => {
    setCurrentDealStage(stage);
    setWorkflowSteps(filterWorkflowSteps(tasksBoard?.results[0].steps, stage));
  };
  const isRepeatableWorkflowStage = (stage: string | null | undefined): boolean =>
    stage === ProjectPhase.ongoingMonitoring && tasksBoard?.results[0].dealUuid == null;

  const hasAccessLevelAndCanEditTemplate = (accessLevel: WorkflowOrganisationAccessLevel): boolean => {
    if (workflowOrganisationAccessLevel === undefined) {
      return false;
    }
    return hasWorkflowOrganisationAccessLevel(accessLevel, workflowOrganisationAccessLevel);
  };

  /*
    Identifies the in-flight board load. Bumped when a load starts and again by the effect's cleanup, so
    work that finishes after the user has left the board can tell it is no longer the current one.
  */
  const fetchRequestId = useRef(0);

  const fetchData = useCallback(async () => {
    fetchRequestId.current += 1;
    const requestId = fetchRequestId.current;

    if (routeWorkflowUuid) {
      const res = await getTaskBoard({
        showComplete,
        paging: {
          afterCursor: null,
          beforeCursor: null,
          limit: 1,
        },
        filter: { results: { uuid: { operator: "eq", value: routeWorkflowUuid } } },
      });
      if (res.status === Status.Success && res.data && res.data.results.length >= 1) {
        const isInitialLoad = tasksBoard === undefined;
        const taskBoardResult = res.data.results[0];
        // The deal parent the board's deal-specific features read, rather than the absence of a project.
        const isDealWorkflow = taskBoardResult.dealUuid !== null;
        const workflowTemplateRes = taskBoardResult.workflowTemplateUuid
          ? await getWorkflowTemplate({
              workflowTemplateUuid: taskBoardResult.workflowTemplateUuid,
            })
          : undefined;
        const orderedWorkflowTemplatePhases =
          workflowTemplateRes?.status === Status.Success && workflowTemplateRes.data
            ? normaliseWorkflowTemplatePhases(workflowTemplateRes.data.phases)
            : [];
        const parsedProjectCachedPhase = parseProjectCachedPhase(taskBoardResult.projectCachedPhase ?? undefined);
        const nextProjectPhase =
          parsedProjectCachedPhase ?? (orderedWorkflowTemplatePhases[0]?.phase as ProjectPhase | undefined);
        setTasksBoard(res.data);
        setWorkflowTemplatePhases(orderedWorkflowTemplatePhases);
        if (!isDealWorkflow && nextProjectPhase) {
          handlePhaseChange(isInitialLoad ? nextProjectPhase : currentPhase, taskBoardResult.steps);
        }
        setProjectDates(() => {
          if (taskBoardResult === undefined) return undefined;

          if (
            taskBoardResult.projectUuid === null ||
            taskBoardResult.projectDisplayName === null ||
            taskBoardResult.projectRowVersion === null
          ) {
            return undefined;
          }

          const parsedJsonDates = JSON.parse(taskBoardResult.dates, jsonReviver) as ProjectDates;

          return {
            uuid: taskBoardResult.projectUuid,
            displayName: taskBoardResult.projectDisplayName,
            rowVersion: taskBoardResult.projectRowVersion,
            currentStatus: "",
            cachedPhase: nextProjectPhase ?? ProjectPhase.feasibility,
            milestones: undefined,
            dates: {
              ...parsedJsonDates,
              ongoingMonitoring: {
                ...parsedJsonDates.ongoingMonitoring,
                cycles: parsedJsonDates.ongoingMonitoring?.cycles?.sort((a, b) => a.sequence - b.sequence) ?? [],
              },
            } as ProjectDates,
            standardSpecificSchemaUuid: taskBoardResult.standardSpecificSchemaUuid ?? null,
          };
        });
        setProjectCachedPhase(nextProjectPhase);
        setWorkflowOrganisationAccessLevel(
          taskBoardResult.organisations.find((x) => x.organisationUuid === currentOrganisationUuid)
            ?.accessLevel as WorkflowOrganisationAccessLevel
        );
        setWorkflowOrganisations(
          taskBoardResult.organisations.map((org) => {
            return {
              uuid: org.organisationUuid,
              displayName: org.name ?? "",
              accessLevel: org.accessLevel,
              role: "",
            };
          })
        );

        if (isDealWorkflow && taskBoardResult.dealUuid) {
          const dealRes = await getDeal({ dealUuid: taskBoardResult.dealUuid });

          if (dealRes.status === Status.Success && dealRes.data) {
            const localDeal = dealRes.data;
            const parsedDealStages = parseDealStages(localDeal.dealDates, orderedWorkflowTemplatePhases);
            const parsedDealDates = parseDealDates(localDeal.dealDates);
            const nextDealStage = getDefaultDealStage(
              parsedDealStages,
              currentDealStage,
              localDeal.stage,
              taskBoardResult.steps[0]?.phase
            );
            setDeal(localDeal);
            setDealStages(parsedDealStages);
            setDealDates(parsedDealDates);
            setCurrentDealStage(nextDealStage);
            setWorkflowSteps(filterWorkflowSteps(taskBoardResult.steps, nextDealStage));
          } else {
            setDeal(undefined);
            setDealStages([]);
            setDealDates(undefined);
            setCurrentDealStage(getWorkflowStageKey(taskBoardResult.steps[0]?.phase) || undefined);
            setWorkflowSteps(filterWorkflowSteps(taskBoardResult.steps, taskBoardResult.steps[0]?.phase));
          }
        } else {
          setDeal(undefined);
          setDealStages([]);
          setDealDates(undefined);
          setCurrentDealStage(undefined);
        }
      } else if (res.status === Status.Success) {
        /*
          The route uuid names no workflow. A general task has no workflow to link to, so its notification
          points here with the task's own uuid (KAN-9609) — resolve it and redirect to where that task is
          listed rather than leaving the user on an undefined board.
        */
        const fallbackRoute = await resolveTasksBoardFallbackRoute(routeWorkflowUuid, currentUserType);

        /*
          Resolving the fallback runs several searches, and the user can leave in the meantime. Both the
          redirect and the error belong to a board that is no longer on screen, and the redirect would
          pull them away from wherever they went next — so a superseded load stops here.
        */
        if (requestId !== fetchRequestId.current) return;

        if (fallbackRoute) {
          navigate(fallbackRoute, { replace: true });
        } else {
          Toast.error({ message: "This tasks board could not be found." });
        }
      } else if (res.status === Status.Error && res.errors && res.errors.length) {
        Toast.error({ message: getErrorMessageFromCode(res.errors[0].message) });
      }
    }
  }, [routeWorkflowUuid, refreshTasksBoard, showComplete, currentOrganisationUuid, currentUserType, navigate]);

  /*  Check if user can edit step or task:
        Owner + Author can edit all steps and tasks,
        Contributor can only edit their own steps and tasks created by there org,
    */
  const canEditStep = (createdByOrganisationUuid: string | null): boolean => {
    if (workflowOrganisationAccessLevel) {
      if (hasAccessLevelAndCanEditTemplate(WorkflowOrganisationAccessLevel.Author)) {
        return true;
      }
      if (
        hasAccessLevelAndCanEditTemplate(WorkflowOrganisationAccessLevel.Contributor) &&
        currentOrganisationUuid !== undefined &&
        currentOrganisationUuid === createdByOrganisationUuid
      ) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    fetchData().finally(() => setIsLoading(false));
    return () => {
      // Leaving the board, or reloading it for another uuid, invalidates the load in flight.
      fetchRequestId.current += 1;
    };
  }, [fetchData, routeWorkflowUuid, refreshTasksBoard]);

  const projectWorkflowStageOptions = (
    workflowTemplatePhases.length
      ? workflowTemplatePhases.map((item) => ({ key: item.phase, value: stringKeyToSentenceCase(item.phase) }))
      : Object.values(ProjectPhase).map((phase) => ({ key: phase, value: stringKeyToSentenceCase(phase) }))
  ) as SelectData;

  const memoisedValue = useMemo(() => {
    const isDealBoardWorkflow = tasksBoard?.results[0].dealUuid != null;

    return {
      projectUuid: tasksBoard?.results[0].projectUuid ?? undefined,
      isDealWorkflow: isDealBoardWorkflow,
      tasksBoard,
      deal,
      projectCachedPhase,
      workflowSteps,
      workflowUuid: routeWorkflowUuid,
      workflowOrganisationAccessLevel,
      workflowOrganisations,
      workflowTemplatePhases,
      dealStages,
      dealDates,
      currentDealStage,
      workflowStageOptions: isDealBoardWorkflow
        ? dealStages.map((stage) => ({ key: stage.key, value: stage.label }))
        : projectWorkflowStageOptions,
      workflowStageLabel: isDealBoardWorkflow ? "Stage" : "Phase",
      showComplete,
      handleShowCompleteChange,

      showCompletePhaseModal,
      setShowCompletePhaseModal,

      showCompleteStageModal,
      setShowCompleteStageModal,

      showEditDatesPanel,
      setShowEditDatesPanel,

      projectDates,
      setProjectDates,

      boardView,
      setBoardView,

      currentPhase,
      handlePhaseChange,
      handleDealStageChange,
      selectedWorkflowStage: isDealBoardWorkflow ? currentDealStage : currentPhase,
      isRepeatableWorkflowStage,

      hasAccessLevelAndCanEditTemplate,
      canEditStep,

      refreshTasksBoard,
      shouldRefreshTasksBoard,
    };
  }, [
    tasksBoard,
    deal,
    projectDates,
    workflowSteps,
    workflowTemplatePhases,
    dealStages,
    dealDates,
    currentPhase,
    currentDealStage,
    boardView,
    refreshTasksBoard,
    currentPhase,
    showCompletePhaseModal,
    showCompleteStageModal,
    showEditDatesPanel,
    handlePhaseChange,
    handleDealStageChange,
    isRepeatableWorkflowStage,
    projectWorkflowStageOptions,
    routeWorkflowUuid,
  ]);

  return <TasksBoardContext.Provider value={memoisedValue}>{!isLoading && children}</TasksBoardContext.Provider>;
};
